import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Switch,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Pagination,
  Skeleton,
  Alert,
  Snackbar,
  Paper,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import RemoveDoneRoundedIcon from "@mui/icons-material/RemoveDoneRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import {
  getBbpsCategories,
  getAdminBbpsBillers,
  setBbpsBillersEnabled,
  getAdminBbpsCharges,
  setAdminBbpsCharge,
} from "../api";

const LIMIT = 20;
const DEFAULT_CATEGORY = "Credit Card";

/* ------------------------------ debounce hook ------------------------------ */
function useDebounced(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function AdminBillers() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [billers, setBillers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [snack, setSnack] = useState(null);

  /* BBPS flat per-role charges */
  const [charges, setCharges] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);

  const debouncedSearch = useDebounced(search);
  const reqId = useRef(0);

  /* Load per-role BBPS charges on mount. */
  useEffect(() => {
    getAdminBbpsCharges()
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.charges || [];
        setCharges(
          list.map((c) => ({ ...c, charge: String(c.charge ?? "") })),
        );
      })
      .catch((e) =>
        setError(e.response?.data?.error || "Failed to load BBPS charges"),
      )
      .finally(() => setChargesLoading(false));
  }, []);

  const handleChargeChange = (roleLevel, value) => {
    const clean = value.replace(/[^0-9.]/g, "");
    setCharges((rows) =>
      rows.map((r) =>
        r.roleLevel === roleLevel ? { ...r, charge: clean } : r,
      ),
    );
  };

  const handleSaveCharge = async (row) => {
    const value = Number(row.charge);
    if (row.charge === "" || isNaN(value) || value < 0) {
      setError("Enter a valid charge (0 or more)");
      return;
    }
    setSavingRole(row.roleLevel);
    setError(null);
    try {
      await setAdminBbpsCharge(row.roleLevel, value);
      setCharges((rows) =>
        rows.map((r) =>
          r.roleLevel === row.roleLevel ? { ...r, charge: String(value) } : r,
        ),
      );
      setSnack(`${row.roleName} charge saved`);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to save charge");
    }
    setSavingRole(null);
  };

  /* Load categories, then default to "Credit Card" (or the first one). */
  useEffect(() => {
    getBbpsCategories()
      .then(({ data }) => {
        const list = data.categories || [];
        setCategories(list);
        const preferred =
          list.find((c) =>
            /credit\s*card/i.test(`${c.key || ""} ${c.label || ""}`),
          ) || list[0];
        if (preferred) setCategory(preferred);
      })
      .catch((e) =>
        setError(e.response?.data?.error || "Failed to load categories"),
      );
  }, []);

  const loadBillers = useCallback(() => {
    if (!category) return;
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    getAdminBbpsBillers({
      category: category.key,
      search: debouncedSearch || undefined,
      page,
      limit: LIMIT,
    })
      .then(({ data }) => {
        if (id !== reqId.current) return;
        setBillers(data.billers || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch((e) => {
        if (id !== reqId.current) return;
        setError(e.response?.data?.error || "Failed to load billers");
      })
      .finally(() => {
        if (id === reqId.current) setLoading(false);
      });
  }, [category, debouncedSearch, page]);

  useEffect(() => {
    loadBillers();
  }, [loadBillers]);

  const handleToggle = async (biller) => {
    const next = !biller.adminEnabled;
    setSavingId(biller.billerId);
    setError(null);
    /* Optimistic flip — revert if the save fails. */
    setBillers((rows) =>
      rows.map((r) =>
        r.billerId === biller.billerId ? { ...r, adminEnabled: next } : r,
      ),
    );
    try {
      await setBbpsBillersEnabled([biller.billerId], next);
      setSnack(`${biller.billerName} ${next ? "enabled" : "disabled"}`);
    } catch (e) {
      setBillers((rows) =>
        rows.map((r) =>
          r.billerId === biller.billerId
            ? { ...r, adminEnabled: biller.adminEnabled }
            : r,
        ),
      );
      setError(e.response?.data?.error || "Failed to update biller");
    }
    setSavingId(null);
  };

  const handleBulk = async (enabled) => {
    const ids = billers.map((b) => b.billerId);
    if (ids.length === 0) return;
    setBulkSaving(true);
    setError(null);
    const prev = billers;
    setBillers((rows) => rows.map((r) => ({ ...r, adminEnabled: enabled })));
    try {
      await setBbpsBillersEnabled(ids, enabled);
      setSnack(
        `${ids.length} biller${ids.length > 1 ? "s" : ""} ${
          enabled ? "enabled" : "disabled"
        }`,
      );
    } catch (e) {
      setBillers(prev);
      setError(e.response?.data?.error || "Failed to update billers");
    }
    setBulkSaving(false);
  };

  const pageCount = Math.ceil(total / LIMIT);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            BBPS Billers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Curate which billers are offered to users in Pay Bills. Disabled
            billers stop appearing in the customer flow.
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, mt: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Card sx={{ p: 3, borderRadius: 3, mt: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          mb={2.5}
        >
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="biller-category">Category</InputLabel>
            <Select
              labelId="biller-category"
              label="Category"
              value={category?.key || ""}
              onChange={(e) => {
                const cat = categories.find((c) => c.key === e.target.value);
                setCategory(cat || null);
                setPage(1);
              }}
            >
              {categories.length === 0 && (
                <MenuItem value="" disabled>
                  {DEFAULT_CATEGORY}
                </MenuItem>
              )}
              {categories.map((c) => (
                <MenuItem key={c.key} value={c.key}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            placeholder="Search billers…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2.5 },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap" gap={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DoneAllRoundedIcon />}
            disabled={bulkSaving || loading || billers.length === 0}
            onClick={() => handleBulk(true)}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Enable all visible
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<RemoveDoneRoundedIcon />}
            disabled={bulkSaving || loading || billers.length === 0}
            onClick={() => handleBulk(false)}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Disable all visible
          </Button>
          {bulkSaving && <CircularProgress size={20} sx={{ alignSelf: "center" }} />}
        </Stack>

        {loading ? (
          <Stack spacing={1.25}>
            {[...Array(6)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={64}
                sx={{ borderRadius: 2.5 }}
              />
            ))}
          </Stack>
        ) : billers.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <SearchRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography fontWeight={600} mb={0.5}>
              No billers found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {search
                ? "Try a different search term"
                : "No billers available in this category"}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.25}>
            {billers.map((b) => {
              const on = !!b.adminEnabled;
              return (
                <Paper
                  key={b.billerId}
                  variant="outlined"
                  sx={{
                    p: 1.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75,
                    borderRadius: 2.5,
                    borderColor: on ? "success.light" : "divider",
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? "rgba(99,102,241,0.16)"
                          : "rgba(99,102,241,0.10)",
                      flexShrink: 0,
                    }}
                  >
                    <AccountBalanceRoundedIcon />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={600} fontSize={14} noWrap>
                      {b.billerName}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      flexWrap="wrap"
                    >
                      {b.coverage && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <PublicRoundedIcon
                            sx={{ fontSize: 13, color: "text.disabled" }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {b.coverage}
                          </Typography>
                        </Stack>
                      )}
                      <Chip
                        size="small"
                        label={on ? "Offered" : "Hidden"}
                        color={on ? "success" : "default"}
                        variant={on ? "filled" : "outlined"}
                        sx={{ fontWeight: 600, fontSize: 10, height: 20 }}
                      />
                    </Stack>
                  </Box>
                  <Switch
                    checked={on}
                    disabled={savingId === b.billerId || bulkSaving}
                    onChange={() => handleToggle(b)}
                    color="success"
                    inputProps={{ "aria-label": `Toggle ${b.billerName}` }}
                  />
                </Paper>
              );
            })}
          </Stack>
        )}

        {pageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      <Card sx={{ p: 2, borderRadius: 3, mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
          <PaymentsRoundedIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" fontWeight={700}>
            BBPS Charges (per role)
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Flat convenience fee added on top of the bill amount, charged to each
          role level when they pay a BBPS bill.
        </Typography>

        {chargesLoading ? (
          <Stack spacing={1.25}>
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={48}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        ) : charges.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No roles available to configure
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 420 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      color: "text.secondary",
                    },
                  }}
                >
                  <TableCell>Role</TableCell>
                  <TableCell>Flat charge (₹)</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {charges.map((row) => (
                  <TableRow key={row.roleLevel} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {row.roleName}
                    </TableCell>
                    <TableCell sx={{ width: 180 }}>
                      <TextField
                        size="small"
                        value={row.charge}
                        onChange={(e) =>
                          handleChargeChange(row.roleLevel, e.target.value)
                        }
                        inputProps={{ min: 0, inputMode: "decimal" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                          sx: { borderRadius: 2 },
                        }}
                        sx={{ maxWidth: 160 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={savingRole === row.roleLevel}
                        onClick={() => handleSaveCharge(row)}
                        startIcon={
                          savingRole === row.roleLevel ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : null
                        }
                        sx={{ borderRadius: 2, fontWeight: 600 }}
                      >
                        {savingRole === row.roleLevel ? "Saving…" : "Save"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ borderRadius: 2 }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
}
